import React from "react";
import PropTypes from "prop-types";
import Flex, { FlexItem } from "../Flex";
import { Small } from "../Typography";
import {
  Bar,
  AppName,
  SearchContainer,
  Separator,
  AiButton,
} from "./Header.styles";
import UserMenu from "./UserMenu";
import AiIcon from "./AiIcon";

/**
 * App chrome header.
 *
 * The avatar/logout control renders when `userMenuContent` is passed. `userName`
 * is only the optional first-row label inside that menu and may be empty —
 * an empty name must not hide Logout.
 */
const Header = ({
  appName,
  userName,
  avatarSource,
  avatarAlt,
  avatarInitials,
  searchComponent,
  dataTestId,
  currentWorkspace,
  logo,
  userMenuContent,
  showLogoSeparator = false,
  onAIClick,
}) => {
  return (
    <Bar dataTestId={dataTestId}>
      <AppName>
        {logo}
        {showLogoSeparator && (
          <Separator display={["none", "none", "none", "block"]} />
        )}
        <Flex flexDirection="column">
          {appName && (
            <FlexItem
              flex="0 0 auto"
              display={["none", "none", "none", "block"]}
            >
              {appName}
            </FlexItem>
          )}
          {currentWorkspace && (
            <FlexItem flex="0 0 auto">
              <Small
                color="subText"
                fontSize="0"
                display={["none", "none", "none", "block"]}
              >
                {currentWorkspace}
              </Small>
            </FlexItem>
          )}
        </Flex>
      </AppName>
      {searchComponent && <SearchContainer>{searchComponent}</SearchContainer>}
      {onAIClick && (
        <AiButton type="button" aria-label="AI Assistant" onClick={onAIClick}>
          <AiIcon />
        </AiButton>
      )}
      {userMenuContent && (
        <UserMenu
          userName={userName}
          avatarSource={avatarSource}
          avatarAlt={avatarAlt}
          avatarInitials={avatarInitials}
          userMenuContent={userMenuContent}
        />
      )}
    </Bar>
  );
};

Header.propTypes = {
  /** Logo for app. Preferably an svg logo */
  logo: PropTypes.node,
  /** Text for app name. Can be a plain text string or an svg logo */
  appName: PropTypes.node,
  /** Current active workspace name. Can be a plain text string or a component */
  currentWorkspace: PropTypes.node,
  /** First-row label in the user menu. Optional; empty omits the row, not the menu. */
  userName: PropTypes.node,
  /** Source path for avatar image. */
  avatarSource: PropTypes.node,
  /** Alt text for avatar image. */
  avatarAlt: PropTypes.string,
  /** Initials for avatar as fallback if there is no image */
  avatarInitials: PropTypes.string,
  /** Can specify a component to be used for searching */
  searchComponent: PropTypes.node,
  /** Applies the chosen theme to the entire header. */
  theme: PropTypes.object,
  /** Allows for use of the `data-testid` attribute for testing. */
  dataTestId: PropTypes.string,
  /** User dropdown body (e.g. Logout). Presence of this node shows the avatar menu. */
  userMenuContent: PropTypes.node,
  /** use this to apply separator if logo and appName or currentWorkspace exists */
  showLogoSeparator: PropTypes.bool,
  /** Function to run when the AI button is clicked */
  onAIClick: PropTypes.func,
};

/** @component */
export default Header;
